import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Star, EyeOff, Eye, Trash2, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TRACK_STATE_LABEL = {
  excelente: 'Excelente',
  bom: 'Bom',
  razoavel: 'Razoável',
  mau: 'Mau',
};

const DIFF_LABEL = {
  easy: 'Fácil',
  moderate: 'Moderada',
  hard: 'Difícil',
  expert: 'Expert',
};

const StarPicker = ({ value, onChange, readOnly = false, size = 'sm' }) => {
  const dim = size === 'lg' ? 'h-6 w-6' : 'h-4 w-4';
  return (
    <div className="flex gap-0.5" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(n)}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition`}
          data-testid={`star-${n}${readOnly ? '-readonly' : ''}`}
          aria-label={`${n} estrelas`}
        >
          <Star
            className={`${dim} ${n <= value ? 'fill-amber-400 text-amber-400' : 'fill-none text-slate-300'}`}
          />
        </button>
      ))}
    </div>
  );
};

const RouteRatings = ({ route, user }) => {
  const [data, setData] = useState({ ratings: [], summary: { count: 0, average_stars: null } });
  const [loading, setLoading] = useState(true);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [trackState, setTrackState] = useState('');
  const [realDifficulty, setRealDifficulty] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!route?.route_id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/routes/${route.route_id}/ratings`, { withCredentials: true });
      setData(res.data);
      const mine = (res.data.ratings || []).find((r) => r.user_id === user?.user_id);
      if (mine) {
        setStars(mine.stars || 0);
        setComment(mine.comment || '');
        setTrackState(mine.track_state || '');
        setRealDifficulty(mine.real_difficulty || '');
      } else {
        setStars(0);
        setComment('');
        setTrackState('');
        setRealDifficulty('');
      }
    } catch (e) {
      // Likely 401 — user not authenticated. Hide silently.
    } finally {
      setLoading(false);
    }
  }, [route?.route_id, user?.user_id]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (stars < 1) {
      toast.error('Escolha pelo menos 1 estrela');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/routes/${route.route_id}/ratings`,
        { stars, comment, track_state: trackState || null, real_difficulty: realDifficulty || null },
        { withCredentials: true },
      );
      toast.success('Avaliação publicada');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao publicar avaliação');
    } finally {
      setSaving(false);
    }
  };

  const deleteMine = async () => {
    if (!window.confirm('Eliminar a sua avaliação?')) return;
    try {
      await axios.delete(`${API}/routes/${route.route_id}/ratings/me`, { withCredentials: true });
      toast.success('Avaliação eliminada');
      load();
    } catch (e) {
      toast.error('Erro ao eliminar');
    }
  };

  const toggleHide = async (rating) => {
    try {
      await axios.patch(
        `${API}/admin/ratings/${rating.rating_id}`,
        { is_hidden: !rating.is_hidden },
        { withCredentials: true },
      );
      toast.success(rating.is_hidden ? 'Avaliação restaurada' : 'Avaliação ocultada');
      load();
    } catch (e) {
      toast.error('Erro de moderação');
    }
  };

  const adminDelete = async (rating) => {
    if (!window.confirm('Eliminar definitivamente esta avaliação?')) return;
    try {
      await axios.delete(`${API}/admin/ratings/${rating.rating_id}`, { withCredentials: true });
      toast.success('Eliminada');
      load();
    } catch (e) {
      toast.error('Erro ao eliminar');
    }
  };

  const myRatingExists = (data.ratings || []).some((r) => r.user_id === user?.user_id);

  if (loading) {
    return <div className="text-xs text-muted-foreground py-3">A carregar avaliações...</div>;
  }

  return (
    <div className="space-y-4" data-testid="route-ratings">
      {/* Summary */}
      <div className="flex items-center justify-between bg-muted/40 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <StarPicker value={Math.round(data.summary.average_stars || 0)} readOnly size="lg" />
          <div>
            <div className="text-sm font-semibold">
              {data.summary.average_stars ? data.summary.average_stars.toFixed(1) : '—'}
              <span className="text-xs text-muted-foreground ml-1">/5</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {data.summary.count} {data.summary.count === 1 ? 'avaliação' : 'avaliações'}
            </div>
          </div>
        </div>
      </div>

      {/* Submit form */}
      {user && (
        <form onSubmit={submit} className="space-y-3 border rounded-lg p-3" data-testid="rating-form">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">
              {myRatingExists ? 'A sua avaliação' : 'Avaliar esta rota'}
            </span>
            <StarPicker value={stars} onChange={setStars} size="lg" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-muted-foreground">Estado do piso</label>
              <Select value={trackState} onValueChange={setTrackState}>
                <SelectTrigger data-testid="track-state-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="excelente">Excelente</SelectItem>
                  <SelectItem value="bom">Bom</SelectItem>
                  <SelectItem value="razoavel">Razoável</SelectItem>
                  <SelectItem value="mau">Mau</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Dificuldade real</label>
              <Select value={realDifficulty} onValueChange={setRealDifficulty}>
                <SelectTrigger data-testid="real-difficulty-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">Fácil</SelectItem>
                  <SelectItem value="moderate">Moderada</SelectItem>
                  <SelectItem value="hard">Difícil</SelectItem>
                  <SelectItem value="expert">Expert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Textarea
            placeholder="Conte como foi… (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            data-testid="rating-comment-input"
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={saving} data-testid="rating-submit">
              {saving ? 'A publicar...' : myRatingExists ? 'Atualizar' : 'Publicar'}
            </Button>
            {myRatingExists && (
              <Button type="button" variant="outline" size="sm" onClick={deleteMine} data-testid="rating-delete-mine">
                <Trash2 className="h-3 w-3 mr-1" /> Eliminar
              </Button>
            )}
          </div>
        </form>
      )}

      {/* Reviews list */}
      <div className="space-y-2" data-testid="ratings-list">
        {(data.ratings || []).length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-3 flex items-center justify-center gap-1">
            <MessageSquare className="h-3 w-3" />
            Sem avaliações ainda. Seja o primeiro!
          </p>
        )}
        {(data.ratings || []).map((r) => (
          <div
            key={r.rating_id}
            className={`border rounded-lg p-3 text-sm ${r.is_hidden ? 'opacity-60 bg-amber-50 border-amber-200' : 'bg-card'}`}
            data-testid={`rating-${r.rating_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-xs">{r.user_name || 'Anónimo'}</span>
                  <StarPicker value={r.stars} readOnly />
                  {r.is_hidden && (
                    <span className="text-[10px] uppercase bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                      Ocultada
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {new Date(r.created_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              </div>
              {user?.role === 'admin' && (
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() => toggleHide(r)}
                    data-testid={`mod-toggle-${r.rating_id}`}
                    title={r.is_hidden ? 'Restaurar' : 'Ocultar'}
                  >
                    {r.is_hidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 px-2"
                    onClick={() => adminDelete(r)}
                    data-testid={`mod-delete-${r.rating_id}`}
                    title="Eliminar"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
            {(r.track_state || r.real_difficulty) && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {r.track_state && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    Piso: {TRACK_STATE_LABEL[r.track_state] || r.track_state}
                  </span>
                )}
                {r.real_difficulty && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                    Dificuldade real: {DIFF_LABEL[r.real_difficulty] || r.real_difficulty}
                  </span>
                )}
              </div>
            )}
            {r.comment && <div className="mt-2 text-slate-700 whitespace-pre-wrap">{r.comment}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RouteRatings;
